import { startCase } from "lodash";
import { FC, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue } from "recoil";

import { ErrorDisplayUnsavedItems } from "@ryde/controls";
import { useConfirmDirtyField, useCrudEditContext } from "@ryde/redux";
import {
  GlobalAppEventEnum,
  IExtendableDto,
  IInjectedDrawerProps,
  isStaleEditFormDto,
  IStationRouteDto,
  IStationRouteEditDrawerProps,
  KeyboardKeys,
  loadingEditFormContentAtomFamily,
  RIGHTBAR_STATION_ROUTE_WIDTH,
  SimpleResult,
  stationRouteHasUnsavedStationsChangeAtom,
  useHasPermissions,
  useIsMobile,
} from "@ryde/shared";

import { BaseDrawer, useFormMapDrawerResize } from "../../../drawers";
import { LayoutRightLoader } from "../../../right";
import { StationRouteFooter } from "./station-route.footer";
import { StationRouteHeader } from "./station-route.header";
import { StationRouteMap } from "./station-route.map";
import { StationRouteDrawerWrapper } from "./station-route.styled";
import { StationRouteTabsWrapper } from "./station-route.tabs.wrapper";

export const StationRouteEditContent: FC<
  IStationRouteEditDrawerProps & IInjectedDrawerProps
> = ({ onCloseDrawer, resolveFn, drawerNumber }) => {
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();
  const { entity, mode, id, data } = useCrudEditContext<IStationRouteDto>();
  const loadingCrudData = useRecoilValue(
    loadingEditFormContentAtomFamily(entity),
  );
  const isStationRouteDataLoading = id
    ? loadingCrudData ||
      isStaleEditFormDto({
        requestedId: id,
        dtoId: data.dto?.id,
        hasDto: Boolean(data.dto),
      })
    : loadingCrudData;
  const unitTitle = t(`${entity}.single`);
  const { hasPermissions: hasUpdatePermission } = useHasPermissions({
    entity: "stationRoute",
    action: "update",
  });

  const [
    stationRouteHasUnsavedStationChange,
    setStationRouteHasUnsavedStationChange,
  ] = useRecoilState(stationRouteHasUnsavedStationsChangeAtom);

  const {
    formState: { isDirty },
  } = useFormContext<Partial<IExtendableDto>>();
  const isStationRouteDirty = isDirty || stationRouteHasUnsavedStationChange;

  const { applyIfConfirmed } = useConfirmDirtyField({
    customContent: <ErrorDisplayUnsavedItems />,
  });

  const onCloseClick = applyIfConfirmed({ isDirty: isStationRouteDirty })(
    (): void => {
      setStationRouteHasUnsavedStationChange(false);
      resolveFn?.(new SimpleResult<IExtendableDto>({ success: false }));
      onCloseDrawer();
    },
  );

  useEffect(() => {
    const closeOnEscapePressed = (e: KeyboardEvent): void => {
      if (e.key === KeyboardKeys.ESCAPE) {
        onCloseClick();
      }
    };

    window.addEventListener("keydown", closeOnEscapePressed);
    return () => window.removeEventListener("keydown", closeOnEscapePressed);
  }, [onCloseClick]);

  const isFormEditable = Boolean(hasUpdatePermission);

  const {
    gridStyle,
    drawerWidthStyle,
    isMapVisible,
    onFormHandleMouseDown,
    onFormHandleKeyDown,
    onMapHandleMouseDown,
    onMapHandleKeyDown,
  } = useFormMapDrawerResize({
    drawerNumber,
    mapResizeEvent: GlobalAppEventEnum.RESIZE_STATION_ROUTE_MAP,
  });

  return (
    <BaseDrawer
      onCloseDrawer={onCloseClick}
      drawerNumber={drawerNumber}
      customWidth={isMobile ? RIGHTBAR_STATION_ROUTE_WIDTH : drawerWidthStyle}
    >
      {!isStationRouteDataLoading && (
        <StationRouteDrawerWrapper style={gridStyle}>
          <StationRouteHeader
            headerTitle={startCase(unitTitle)}
            closeClick={onCloseClick}
          />
          {!isMobile && (
            // eslint-disable-next-line jsx-a11y-x/no-noninteractive-element-interactions -- intentional: keyboard-operable resize handle
            <div
              className="StationRouteDrawer-LeftHandle"
              onMouseDown={onFormHandleMouseDown}
              onKeyDown={onFormHandleKeyDown}
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize form section"
              // eslint-disable-next-line jsx-a11y-x/no-noninteractive-tabindex -- intentional: focusable resize handle operated via onKeyDown
              tabIndex={0}
            />
          )}
          <StationRouteTabsWrapper />

          {isFormEditable && (
            <StationRouteFooter
              onCloseDrawer={onCloseDrawer}
              resolveFn={resolveFn}
            />
          )}
          {!isMobile && (
            // eslint-disable-next-line jsx-a11y-x/no-noninteractive-element-interactions -- intentional: keyboard-operable resize handle
            <div
              className="StationRouteDrawer-MiddleHandle"
              onMouseDown={onMapHandleMouseDown}
              onKeyDown={onMapHandleKeyDown}
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize map section"
              // eslint-disable-next-line jsx-a11y-x/no-noninteractive-tabindex -- intentional: focusable resize handle operated via onKeyDown
              tabIndex={0}
            />
          )}
          {!isMobile && !isStationRouteDataLoading && isMapVisible && (
            <StationRouteMap mode={mode} />
          )}
        </StationRouteDrawerWrapper>
      )}
      {isStationRouteDataLoading && (
        <LayoutRightLoader>{t(`ui.LOADING_DATA`)}</LayoutRightLoader>
      )}
    </BaseDrawer>
  );
};
